import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { adminApi } from "../../api/resources";
import type { DashboardSummary } from "../../api/types";
import { PageHeader } from "../../components/ComingSoon";
import { DemoBadge } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";
import { LoadState, useLoad } from "./shared";
import { PayTiles } from "./pay/OverviewTab";
import { PaymentsDashboard } from "./payments/PaymentsDashboard";

type Card = { key: keyof DashboardSummary; label: string; to: string; tone?: string };
const CARDS: Card[] = [
  { key: "employees_active", label: "Active employees", to: "/admin/employees?tab=employees&status=ACTIVE" },
  { key: "employees_registered_unverified", label: "Registered – phone unverified", to: "/admin/employees?tab=employees&status=REGISTERED_UNVERIFIED" },
  { key: "employees_deactivated", label: "Deactivated", to: "/admin/employees?tab=employees&status=DEACTIVATED" },
  { key: "invitations_open", label: "Open invitations", to: "/admin/employees?tab=invitations&status=PENDING" },
  { key: "invitations_needs_review", label: "Needs review", to: "/admin/employees?tab=review", tone: "text-signal" },
  { key: "invitations_expiring_48h", label: "Invites expiring in 48h", to: "/admin/employees?tab=invitations&status=EXPIRING_48H", tone: "text-warn" },
  { key: "venues_active", label: "Active venues", to: "/admin/venues" },
  { key: "shifts_needs_reassignment", label: "Needs reassignment (declined)", to: "/admin/shift-assignments?tab=list", tone: "text-signal" },
  { key: "crew_alerts_24h", label: "Late / decline texts (24h)", to: "/admin/shift-assignments?tab=list", tone: "text-warn" },
  { key: "shifts_upcoming_7d", label: "Upcoming shifts (next 7 days)", to: "/admin/shift-assignments?tab=list&scope=next7" },
  { key: "shifts_needing_attention", label: "Shifts needing attention", to: "/admin/shift-assignments?tab=attention", tone: "text-signal" },
  { key: "clock_clocked_in", label: "Clocked in now", to: "/admin/timeclock-activity", tone: "text-emerald-300" },
  { key: "clock_pending_gps", label: "Pending GPS", to: "/admin/timeclock-activity", tone: "text-sky-300" },
  { key: "clock_missing_out", label: "Missing OUT", to: "/admin/corrections?code=MISSING_OUT", tone: "text-warn" },
  { key: "clock_open_exceptions", label: "Open exceptions", to: "/admin/corrections", tone: "text-signal" },
  { key: "phones_verified", label: "Verified phones", to: "/admin/employees?tab=employees", tone: "text-emerald-300" },
  { key: "phones_unverified", label: "Unverified phones", to: "/admin/employees?tab=employees&status=REGISTERED_UNVERIFIED", tone: "text-warn" },
  { key: "phones_opted_out", label: "Opted out of texts", to: "/admin/sms-activity", tone: "text-warn" },
  { key: "phone_conflicts_open", label: "Open phone conflicts", to: "/admin/employees?tab=conflicts", tone: "text-signal" },
];

const SECTIONS: { slug: string; title: string; keys: (keyof DashboardSummary)[] }[] = [
  { slug: "live-timeclock", title: "Live Timeclock", keys: ["clock_clocked_in", "clock_pending_gps", "clock_missing_out", "clock_open_exceptions"] },
  { slug: "shifts-venues", title: "Shifts & Venues", keys: ["shifts_upcoming_7d", "shifts_needing_attention", "shifts_needs_reassignment", "crew_alerts_24h", "venues_active"] },
  { slug: "crew", title: "Crew", keys: ["employees_active", "employees_registered_unverified", "employees_deactivated"] },
  { slug: "invitations", title: "Invitations", keys: ["invitations_open", "invitations_needs_review", "invitations_expiring_48h"] },
  { slug: "phones-texts", title: "Phones & Texts", keys: ["phones_verified", "phones_unverified", "phones_opted_out", "phone_conflicts_open"] },
];
const BY_KEY = Object.fromEntries(CARDS.map((c) => [c.key, c])) as Record<string, Card>;

function CountCard({ c, n }: { c: Card; n: number }) {
  return (
    <Link to={c.to} data-testid={`dashboard-card-${c.key}`}
      className="group flex min-h-[96px] flex-col justify-between rounded-[14px] border border-white/10 bg-surface px-4 py-3.5 transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-signal/50 hover:shadow-[0_0_24px_-10px_rgba(255,29,0,0.6)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/70 motion-reduce:transition-none">
      <p className="label-caps line-clamp-2 flex items-start gap-2 text-[10.5px] leading-snug text-zinc-500 group-hover:text-zinc-300">{c.key === "clock_clocked_in" && n > 0 && <span aria-hidden className="pulse-dot mt-1 h-2 w-2 shrink-0 rounded-full bg-signal shadow-[0_0_8px_rgba(255,29,0,0.9)]" />}{c.label}</p>
      <div className="mt-2 flex items-baseline gap-2">
        <p data-testid={`dashboard-count-${c.key}`} className={`font-display text-3xl font-light leading-none ${n > 0 ? c.tone ?? "text-white" : "text-zinc-600"}`}>{n}</p>
        {n === 0 && <p className="text-[11px] text-zinc-600">None right now</p>}
      </div>
    </Link>
  );
}

export default function AdminDashboard() {
  const { session } = useAuth();
  const admin = session?.admin;
  const { data, error } = useLoad(adminApi.dashboard);
  return (
    <section data-testid="admin-dashboard-page" className="animate-rise">
      <PageHeader title="Dashboard">
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <ShieldCheck className="h-4 w-4 text-signal" />
          <span data-testid="dashboard-admin-name">{admin?.name}</span>
          <DemoBadge show={admin?.is_demo ?? false} testId="dashboard-admin-demo-badge" />
          <span data-testid="dashboard-backup-codes-remaining" className="text-xs text-zinc-500">· {admin?.backup_codes_remaining ?? 0} backup codes left</span>
        </div>
      </PageHeader>
      <LoadState error={error} loading={!data} testId="dashboard-load-error" />
      {data && (
        <>
          <div className="space-y-7">
            {SECTIONS.map((sec) => (
              <section key={sec.slug} data-testid={`dashboard-section-${sec.slug}`} aria-labelledby={`dash-h-${sec.slug}`}>
                <div className="mb-3 flex items-center gap-3">
                  <h2 id={`dash-h-${sec.slug}`} className="label-caps shrink-0 text-zinc-400">{sec.title}</h2>
                  <span aria-hidden className="h-px flex-1 bg-gradient-to-r from-white/15 to-transparent" />
                </div>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                  {sec.keys.map((k) => <CountCard key={k} c={BY_KEY[k]} n={data[k] as number} />)}
                </div>
              </section>
            ))}
          </div>
          <PayTiles />
          <div className="mt-8"><p className="label-caps mb-3 text-zinc-500">Manual payments</p><PaymentsDashboard /></div>
          {data.includes_demo && (
            <p data-testid="dashboard-includes-demo-note" className="mt-6 text-xs text-blue-400">Counts include DEMO data (Preview environment).</p>
          )}
        </>
      )}
    </section>
  );
}
